import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getPriceAnalysisData,
  getMonthlyPriceForecast,
  getForecastMetrics,
} from "../../../Rest_api/RestApi";
import { PriceAnalysisData } from "../../../models/priceAnalysis";
import { COLOR_CODES } from "../../../common";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import Loading from "../../../components/Loading";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import FilterMenu from "../../../components/FilterMenu";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { ExchangeDataTabs } from "../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { BarChart3, Wallet } from "lucide-react";

export const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    apiKey: "slot",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    apiKey: "day",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week",
    apiKey: "week",
    values: ["week"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    apiKey: "month",
    values: ["month"],
    isTime: false,
  },
];

const PRICE_TYPE_OPTIONS = [
  { key: "simple_avg", label: "Simple Avg." },
  { key: "wt_avg", label: "Wt. Avg." },
];

const H_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  // { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

const variableFilterOptions = [
  { key: "actual", label: "Current" },
  { key: "gna_forecast", label: "GNA Forecast" },
  { key: "prev_year", label: "Prev Year" },
  { key: "rolling_avg", label: "30-Day Roll. Avg." },
  { key: "prev_year_rolling_avg", label: "Prev Year 30-Day Roll. Avg." },
];

export function formatDateToLocal(date: any) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const PriceForcastingIEXDAM = () => {
  const [priceAnalysisData, setPriceAnalysisData] = useState<PriceAnalysisData[]>([]);
  const [monthlyRawData, setMonthlyRawData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedPriceType, setSelectedPriceType] = useState(PRICE_TYPE_OPTIONS[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedVariableOptions, setSelectedVariableOptions] = useState<string[]>(
    variableFilterOptions.map((opt) => opt.key)
  );
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<{
    weighted_avg_price: number | null;
    last_year_wt_avg_price: number | null;
    forecasted_wt_avg_price: number | null;
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const metricsRequestIdRef = useRef(0);

  const isMonthInterval = selectedInterval.key === "month";

  // Read from Redux store
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);

  const dates = DefaultDates?.[activeTab]?.defaultRangeDate;

  // ── Fetch forecast metrics ──
  const fetchMetrics = useCallback(async () => {
    if (!dates) return;
    const requestId = ++metricsRequestIdRef.current;
    setMetricsLoading(true);
    try {
      const startDateStr = formatDateToLocal(dates.startDate);
      const endDateStr = formatDateToLocal(dates.endDate);
      if (startDateStr === "NaN-NaN-NaN" || endDateStr === "NaN-NaN-NaN") {
        if (requestId === metricsRequestIdRef.current) {
          setMetricsLoading(false);
        }
        return;
      }
      const data = await getForecastMetrics(startDateStr, endDateStr, "IEX", "DAM");
      if (requestId === metricsRequestIdRef.current) {
        setMetrics(data);
      }
    } catch (error) {
      console.error("Error fetching forecast metrics:", error);
      if (requestId === metricsRequestIdRef.current) {
        setMetrics(null);
      }
    } finally {
      if (requestId === metricsRequestIdRef.current) {
        setMetricsLoading(false);
      }
    }
  }, [dates]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const formatMetricValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "--";
    return typeof value === "number" ? value.toFixed(2) : "--";
  };

  // ── Fetch for Time Slot / Date / Week intervals ──
  const _handlefetchData = useCallback(async () => {
    if (isMonthInterval || !dates) return;
    setLoading(true);

    try {
      const startDateStr = formatDateToLocal(dates.startDate);
      const endDateStr = formatDateToLocal(dates.endDate);

      if (startDateStr === "NaN-NaN-NaN" || endDateStr === "NaN-NaN-NaN") {
        setLoading(false);
        return;
      }

      const analysisData = await getPriceAnalysisData({
        interval: selectedInterval.apiKey as "slot" | "day" | "week" | "month",
        price_type: selectedPriceType.key as "simple_avg" | "wt_avg",
        exchange_name: "IEX",
        product_name: "DAM",
        start_date: startDateStr,
        end_date: endDateStr,
      });

      setPriceAnalysisData(
        analysisData.map((item) => ({
          ...item,
          price: item.price / 1000,
          prev_year_price: item.prev_year_price != null ? item.prev_year_price / 1000 : null,
          rolling_actual_30_day_avgs: item.rolling_actual_30_day_avgs != null ? item.rolling_actual_30_day_avgs / 1000 : null,
          prev_year_rolling_actual_30_day_avgs: item.prev_year_rolling_actual_30_day_avgs != null ? item.prev_year_rolling_actual_30_day_avgs / 1000 : null,
          gna_forecasted_price: item.gna_forecasted_price != null ? item.gna_forecasted_price / 1000 : null,
        }))
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [dates, selectedPriceType.key, selectedInterval.apiKey, isMonthInterval]);

  // ── Fetch for Month interval (monthly forecast API) ──
  const _handleFetchMonthly = useCallback(async () => {
    if (!isMonthInterval) return;
    setLoading(true);

    try {
      const rangeData = await getMonthlyPriceForecast(
        selectedPriceType.key as "simple_avg" | "wt_avg"
      );

      const transformedData = rangeData
        .map((item: any) => ({
          ...item,
          actual_mcp: item.avg_price_actual,
          actual_wt_avg_mcp: item.actual_wt_avg_price,
          month: `${monthNames[item.month - 1]} ${item.year}`,
          monthGroup: monthNames[item.month - 1],
        }))
        .sort(
          (a: { monthGroup: string }, b: { monthGroup: string }) =>
            monthNames.indexOf(a.monthGroup) - monthNames.indexOf(b.monthGroup)
        );

      setMonthlyRawData(transformedData);
    } catch (error) {
      console.error("Error fetching monthly data:", error);
    } finally {
      setLoading(false);
    }
  }, [isMonthInterval, selectedPriceType.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    _handleFetchMonthly();
  }, [_handleFetchMonthly]);

  // ── Process normal interval data ──
  useEffect(() => {
    if (isMonthInterval) return;

    let filteredData = priceAnalysisData;

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((b) => b.value).includes(item.x_axis)
      );
    }

    const transformedData = filteredData.map((item) => ({
      x_axis: selectedInterval.key === "week" && item.second_x_axis ? `${item.x_axis}\n(${item.second_x_axis})` : item.x_axis,
      price: item.price,
      prev_year_price: item.prev_year_price,
      rolling_actual_30_day_avgs: item.rolling_actual_30_day_avgs,
      prev_year_rolling_actual_30_day_avgs: item.prev_year_rolling_actual_30_day_avgs,
      gna_forecasted_price: item.gna_forecasted_price,
      slot: item.x_axis,
      date: item.second_x_axis || item.x_axis,
      second_x_axis: item.second_x_axis,
    }));

    setChartData(transformedData);
    setTableData(transformedData);
    setChartKeys(
      [
        "price",
        "prev_year_price",
        "rolling_actual_30_day_avgs",
        "prev_year_rolling_actual_30_day_avgs",
        "gna_forecasted_price",
      ].filter((key) =>
        transformedData.some(
          (item) =>
            item[key as keyof typeof item] !== null &&
            item[key as keyof typeof item] !== undefined
        )
      )
    );
  }, [priceAnalysisData, selectedBlock, isMonthInterval]);

  // ── Process monthly data ──
  useEffect(() => {
    if (!isMonthInterval) return;

    const suffixKey =
      selectedPriceType.key === "wt_avg" ? "actual_wt_avg_mcp" : "actual_mcp";

    const par: ChartDataParams = {
      keys: ["monthGroup"],
      tableKeys: ["monthGroup", "month", "year"],
      groupBy: ["year"],
      volumeKey: "",
      aggregationKeys: [suffixKey],
      priceKey: "",
      data: monthlyRawData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [monthlyRawData, isMonthInterval, selectedPriceType.key]);

  const xDataKey = isMonthInterval ? "monthGroup" : "x_axis";

  // ── Chart suffix helper for monthly ──
  const getMonthlyChartSuffix = () => {
    if (selectedPriceType.key === "wt_avg") {
      return { label: "", value: "actual_wt_avg_mcp" };
    }
    return { label: "", value: "actual_mcp" };
  };

  // ── Legends for normal intervals ──
  function getNormalLegends() {
    const legends: any[] = [];

    if (selectedVariableOptions.includes("actual")) {
      legends.push({
        name: "Current",
        dataKey: "price",
        stroke: COLOR_CODES.primary,
        unit: "Rs/KWh",
        type: selectedChartType.value,
        stackId: selectedChartType.stackId,
      });
    }

    const hasGnaForecastData = priceAnalysisData.some(
      (item) =>
        item.gna_forecasted_price !== null &&
        item.gna_forecasted_price !== undefined
    );
    if (hasGnaForecastData && selectedVariableOptions.includes("gna_forecast")) {
      legends.push({
        name: "GNA Forecast",
        dataKey: "gna_forecasted_price",
        stroke: "#F15CE2",
        unit: "Rs/KWh",
        type: selectedChartType.value,
        stackId: selectedChartType.stackId,
      });
    }

    const hasPrevYearData = priceAnalysisData.some(
      (item) => item.prev_year_price !== null && item.prev_year_price !== undefined
    );
    if (hasPrevYearData && selectedVariableOptions.includes("prev_year")) {
      legends.push({
        name: "Prev Year",
        dataKey: "prev_year_price",
        stroke: "#7CBF00",
        unit: "Rs/KWh",
        type: selectedChartType.value,
        stackId: selectedChartType.stackId,
      });
    }

    const hasRollingData = priceAnalysisData.some(
      (item) =>
        item.rolling_actual_30_day_avgs !== null &&
        item.rolling_actual_30_day_avgs !== undefined
    );
    if (hasRollingData) {
      if (selectedVariableOptions.includes("rolling_avg")) {
        legends.push({
          name: "30-Day Roll. Avg.",
          dataKey: "rolling_actual_30_day_avgs",
          stroke: "#213E77",
          unit: "Rs/KWh",
          type: selectedChartType.value,
          stackId: selectedChartType.stackId,
        });
      }
      if (selectedVariableOptions.includes("prev_year_rolling_avg")) {
        legends.push({
          name: "Prev Year 30-Day Roll. Avg.",
          dataKey: "prev_year_rolling_actual_30_day_avgs",
          stroke: "#639FCB",
          unit: "Rs/KWh",
          type: selectedChartType.value,
          stackId: selectedChartType.stackId,
        });
      }
    }

    return legends;
  }

  // ── Legends for monthly interval (year-wise bars) ──
  // Fixed colors per year so each year always has the same color
  const YEAR_COLORS: Record<string, string> = {
    "2023": "#639FCB",
    "2024": "#7CBF00",
    "2025": "#F1935C",
    "2026": "#213E77",
    "2027": "#F15CE2",
    "2028": "#00B4D8",
  };
  const DEFAULT_YEAR_COLORS = ["#639FCB", "#7CBF00", "#F1935C", "#213E77", "#F15CE2", "#00B4D8"];

  function getMonthlyLegends() {
    const { label, value } = getMonthlyChartSuffix();

    const currentMonth = monthNames[new Date().getMonth()];
    const currentYear = new Date().getFullYear().toString();

    // Sort chartKeys by year to ensure consistent legend + chart order
    const sortedKeys = [...chartKeys].sort((a, b) => {
      const yearA = parseInt(a.split(" ")[1]) || 0;
      const yearB = parseInt(b.split(" ")[1]) || 0;
      return yearA - yearB;
    });

    return sortedKeys.map((key, index) => {
      const [barMonth, barYear] = key.split(" ");
      const isCurrentMonthYear =
        barMonth === currentMonth && barYear === currentYear;

      // Use fixed color for the year, fallback to palette by index
      const color = YEAR_COLORS[barYear] || DEFAULT_YEAR_COLORS[index % DEFAULT_YEAR_COLORS.length];

      return {
        dataKey: `${key}_${value}`,
        name: `${isCurrentMonthYear ? "Forecast " : ""}${key
          .replace("DAM", "")
          .replace("_", " ")
          .replace("undefined", "")} ${label}`,
        stroke: color,
        legendColor: color,
        type: ChartType.Bar,
      };
    });
  }

  const legends = useMemo(() => {
    return isMonthInterval ? getMonthlyLegends() : getNormalLegends();
  }, [chartKeys, isMonthInterval, selectedChartType, selectedPriceType.key, priceAnalysisData, selectedVariableOptions]);

  // ▶ Legend filter hook — syncs chart legend clicks with table columns
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: isMonthInterval
      ? ["monthGroup"]
      : selectedInterval.isTime
        ? ["x_axis", "second_x_axis"]
        : ["x_axis"],
    extraLabels: isMonthInterval
      ? ["Month"]
      : selectedInterval.isTime
        ? ["Time Slot", "Date"]
        : [selectedInterval.label],
    filename: "Price Forecasting IEX DAM",
    showTotal: false,
  };

  const currentMonthName = monthNames[new Date().getMonth()];
  const currentYear = new Date().getFullYear();

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
              setSelectedBlock([]);
            }}
          />

          <FilterMenu
            title="Price Type"
            items={PRICE_TYPE_OPTIONS.map((option) => option.label)}
            selectedItems={[selectedPriceType.label]}
            onSelect={(item, index) => {
              setSelectedPriceType(PRICE_TYPE_OPTIONS[index]);
            }}
          />

          {!isMonthInterval && (
            <FilterMenu
              title="Variable"
              items={variableFilterOptions.map((item) => item.label)}
              selectedItems={variableFilterOptions
                .filter((opt) => selectedVariableOptions.includes(opt.key))
                .map((opt) => opt.label)}
              setSelectedItems={(selectedItems: string[]) => {
                const newSelectedKeys = variableFilterOptions
                  .filter((opt) => selectedItems.includes(opt.label))
                  .map((opt) => opt.key);
                setSelectedVariableOptions(newSelectedKeys);
              }}
            />
          )}
          {!isMonthInterval && (
            <FilterMenu
              title="Chart Type"
              items={H_CHART_TYPES.map((item) => item.label)}
              selectedItems={[selectedChartType.label]}
              onSelect={(item, index) => {
                setSelectedChartType(H_CHART_TYPES[index]);
              }}
            />
          )}
        </div>
      </NewFilterContainer>

      <MainPageContainer className="!justify-start">
        <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
        <div className="gas-chart-container">
          <div className="gas-heading grid grid-cols-3 items-center mb-4 min-h-[56px] h-auto flex-none flex-shrink-0 py-1">
            <h2 className="chartHeading">DAM Price Forecast (IEX)</h2>

            <div className="flex justify-center items-center h-full">
              {/* Metrics Cards - Show in both Chart and Summary view */}
              {!metricsLoading && metrics && (dataTab?.label === "Chart" || dataTab?.label === "Summary" || dataTab?.label === "Table") && (
                <div className="flex gap-4">
                  {/* Chip 1: Predicted Wt. Avg Price */}
                  <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                    <div className="bg-orange-100 p-1.5 rounded-full">
                      <Wallet className="w-3.5 h-3.5 text-orange-600" />
                    </div>
                    <div className="flex flex-col gap-0">
                      <span className="text-[10px] text-gray-500 font-medium leading-tight">Predicted Wt. Avg Price</span>
                      <span className="text-xs font-bold text-gray-800 leading-tight">
                        {formatMetricValue(metrics?.weighted_avg_price)}
                        <span className="text-[9px] text-gray-400 font-normal ml-0.5">
                          Rs/KWh
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Chip 2: Forecasted Wt. Avg Price */}
                  <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                    <div className="bg-purple-100 p-1.5 rounded-full">
                      <BarChart3 className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    <div className="flex flex-col gap-0">
                      <span className="text-[10px] text-gray-500 font-medium leading-tight">Forecasted Wt. Avg Price</span>
                      <span className="text-xs font-bold text-gray-800 leading-tight">
                        {formatMetricValue(metrics?.forecasted_wt_avg_price)}
                        <span className="text-[9px] text-gray-400 font-normal ml-0.5">
                          Rs/KWh
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Chip 3: Last Year Wt. Avg Price */}
                  <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
                    <div className="bg-blue-100 p-1.5 rounded-full">
                      <BarChart3 className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div className="flex flex-col gap-0">
                      <span className="text-[10px] text-gray-500 font-medium leading-tight">Last Year Wt. Avg Price</span>
                      <span className="text-xs font-bold text-gray-800 leading-tight">
                        {formatMetricValue(metrics?.last_year_wt_avg_price)}
                        <span className="text-[9px] text-gray-400 font-normal ml-0.5">
                          Rs/KWh
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end items-center h-full">
              {selectedInterval.isTime && (
                <BlockRangeSelector
                  blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                  placeholder="Select Block"
                  selectedOptions={selectedBlock.map((item) => item.value)}
                  setSelectedOptions={setSelectedBlock}
                />
              )}
            </div>
          </div>
          <div className="h-full w-full relative">
            {dataTab?.label === "Chart" && (
              <ReMixChart
                data={chartData}
                showBrush={chartData.length > 96 ? true : undefined}
                brushStart={isMonthInterval ? BrushStart.Start : BrushStart.End}
                isTimeSlot={selectedInterval.isTime}
                onlyTitle={true}
                barCategoryGap={10}
                unit={"Rs/KWh"}
                nameGap={40}
                yAxisLabel={"Rs/KWh"}
                yMaxValue={isMonthInterval ? 10 : undefined}
                legends={legends as any}
                onLegendSelect={handleLegendSelect}
                defaultSelectedLegends={
                  selectedLegends.length > 0
                    ? selectedLegends
                    : isMonthInterval
                      ? legends.filter((legend: any) =>
                        legend.name.includes(currentYear.toString())
                      )
                      : undefined
                }
                customLegendOrder={
                  isMonthInterval
                    ? undefined
                    : [
                      "Current",
                      "GNA Forecast",
                      "Prev Year",
                      "30-Day Roll. Avg.",
                      "Prev Year 30-Day Roll. Avg.",
                    ]
                }
                xDataKey={xDataKey}
                isPriceForecast={isMonthInterval ? true : undefined}
                xTick={
                  selectedInterval.isTime && selectedBlock.length == 0
                    ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                    : isMonthInterval
                      ? (value: any, index: number) => {
                        return renderHourTick(value, 10);
                      }
                      : undefined
                }
                secondXDataKey={
                  selectedInterval.isTime ? "second_x_axis" : undefined
                }
              />
            )}
            {dataTab?.label === "Summary" && (
              <div
                style={isMonthInterval ? { height: "calc(100vh - 240px)" } : undefined}
                className={isMonthInterval ? "overflow-auto" : ""}
              >
                <LegendSummaryTable {...(tablePar as any)} />
              </div>
            )}
            {dataTab?.label === "Table" && (
              <ChartTable {...(tablePar as any)} />
            )}
            {isMonthInterval && (
              <div className="flex justify-between items-center absolute bottom-0 left-0 right-0">
                <p
                  style={{ fontSize: "0.85rem", color: "#666", marginTop: "8px" }}
                >
                  <em>Note:</em> (1) MCP reflects a{" "}
                  {selectedPriceType.key === "wt_avg" ? "weighted" : "simple"}{" "}
                  average, (2) The values for{" "}
                  <strong>
                    {currentMonthName} {currentYear}
                  </strong>{" "}
                  are forecasted estimates.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 items-center">
            <div className="col-span-1"></div>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer >
    </div >
  );
};

export default PriceForcastingIEXDAM;
